import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import { apiFetch } from '../../../api/apiClient';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrochip } from '@fortawesome/free-solid-svg-icons';

const PAGE_LENGTH_OPTIONS = [20, 50, 100, -1];
const PAGE_LENGTH_LABELS = [20, 50, 100, 'All'];
const DC_BLOCK_STRINGS_MIN = 12;
const DC_BLOCK_STRINGS_MAX = 24;

const HEATMAP_RED = [0xBB, 0x00, 0x00];
const HEATMAP_YELLOW = [0xBB, 0xBB, 0x00];
const HEATMAP_GREEN = [0x00, 0xBB, 0x00];

const HEATMAP_GRADIENT_STOPS = Array.from({ length: 11 }, (_, index) => {
  if (index <= 5) {
    return rgbToHex(lerpRgb(HEATMAP_RED, HEATMAP_YELLOW, index / 5));
  }

  return rgbToHex(lerpRgb(HEATMAP_YELLOW, HEATMAP_GREEN, (index - 5) / 5));
});

function lerpChannel(start, end, amount) {
  return Math.round(start + ((end - start) * amount));
}

function lerpRgb(startRgb, endRgb, amount) {
  return [
    lerpChannel(startRgb[0], endRgb[0], amount),
    lerpChannel(startRgb[1], endRgb[1], amount),
    lerpChannel(startRgb[2], endRgb[2], amount),
  ];
}

function rgbToHex([red, green, blue]) {
  return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function isNormalizedView(tableView) {
  return tableView !== 'strings';
}

function getNormalizedValueStats(rows) {
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let count = 0;

  rows.forEach((row) => {
    row.values.forEach((value) => {
      if (value === '' || value == null) {
        return;
      }

      const num = Number(value);
      if (!Number.isNaN(num)) {
        min = Math.min(min, num);
        max = Math.max(max, num);
        sum += num;
        count += 1;
      }
    });
  });

  if (count === 0) {
    return { min: 0, max: 0, avg: 0 };
  }

  return { min, max, avg: sum / count };
}

function getHeatmapColor(value, min, max, avg) {
  if (min === max) {
    return HEATMAP_GRADIENT_STOPS[5];
  }

  if (value <= avg) {
    if (avg === min) {
      return HEATMAP_GRADIENT_STOPS[5];
    }

    const normalized = (value - min) / (avg - min);
    const stopIndex = Math.round(normalized * 5);
    return HEATMAP_GRADIENT_STOPS[stopIndex];
  }

  if (avg === max) {
    return HEATMAP_GRADIENT_STOPS[5];
  }

  const normalized = (value - avg) / (max - avg);
  const stopIndex = Math.min(10, 5 + Math.round(normalized * 5));
  return HEATMAP_GRADIENT_STOPS[stopIndex];
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatNormalizedInputDate(date) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}

function formatNormalizedDisplayDate(date) {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function parseNormalizedInputDate(dateValue) {
  const [year, month, day] = dateValue.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function getDCBlocksQueryParams(blockSet, tableView, selectedNormalizedDate) {
  const params = new URLSearchParams({ designation: blockSet });

  if (tableView === 'strings') {
    params.set('view', 'strings');
  } else if (tableView === 'normalized-current') {
    params.set('view', 'normalized');
    params.set('normalizedcontext', 'current');
  } else if (tableView === 'normalized-previous') {
    params.set('view', 'normalized');
    params.set('normalizedcontext', 'previous');
    if (selectedNormalizedDate) {
      params.set('normalizeddate', selectedNormalizedDate);
    }
  } else if (tableView === 'normalized-comparison') {
    params.set('view', 'normalized');
    params.set('normalizedcontext', 'comparison');
    if (selectedNormalizedDate) {
      params.set('normalizeddate', selectedNormalizedDate);
    }
  }

  return params;
}

function destroyDataTable(dataTableRef, restore = true) {
  if (!dataTableRef.current) {
    return;
  }

  try {
    dataTableRef.current.destroy(restore);
  } catch (error) {
    console.warn('Failed to destroy DataTable instance:', error);
  } finally {
    dataTableRef.current = null;
  }
}

function resetTableHost(tableHostRef, dataTableRef) {
  destroyDataTable(dataTableRef);

  if (tableHostRef.current) {
    tableHostRef.current.innerHTML = '';
  }
}

function formatTableInfo(_settings, start, end, _max, total) {
  if (total === 0) {
    return 'Showing 0 to 0 out of 0 entries';
  }

  return `Showing ${start} to ${end} out of ${total} entries`;
}

function formatComparisonPercent(value) {
  if (value === '' || value == null) {
    return '';
  }

  const num = Number(value);
  if (Number.isNaN(num)) {
    return value;
  }

  return `${(num * 100).toFixed(1)}%`;
}

function parseChannelIndexFromHeader(header) {
  const match = String(header || '').trim().toUpperCase().match(/^CH(\d+)$/);
  if (!match) {
    return null;
  }

  const index = Number(match[1]);
  return Number.isInteger(index) && index >= 1 ? index : null;
}

function validateNumStringsInput(value) {
  const trimmed = String(value ?? '').trim();
  if (!/^\d+$/.test(trimmed)) {
    return 'Enter a whole number.';
  }

  const parsed = Number(trimmed);
  if (parsed < DC_BLOCK_STRINGS_MIN) {
    return `Value must be at least ${DC_BLOCK_STRINGS_MIN}.`;
  }

  if (parsed > DC_BLOCK_STRINGS_MAX) {
    return `Value must be no more than ${DC_BLOCK_STRINGS_MAX}.`;
  }

  return null;
}

function setStringsEditError(activeEditor, message) {
  if (!activeEditor?.errorEl || !activeEditor?.input) {
    return;
  }

  if (!message) {
    activeEditor.errorEl.hidden = true;
    activeEditor.errorEl.textContent = '';
    activeEditor.input.classList.remove('is-invalid');
    return;
  }

  activeEditor.errorEl.hidden = false;
  activeEditor.errorEl.textContent = message;
  activeEditor.input.classList.add('is-invalid');
}

function updateRowStringsValue(rows, deviceId, channelIndex, nextValue) {
  return rows.map((row) => {
    if (row.id !== deviceId) {
      return row;
    }

    const values = [...row.values];
    const valueIndex = channelIndex - 1;
    while (values.length <= valueIndex) {
      values.push('');
    }
    values[valueIndex] = nextValue;
    return { ...row, values };
  });
}

function closeActiveStringsEditor(activeEditorRef) {
  const activeEditor = activeEditorRef.current;
  if (!activeEditor) {
    return;
  }

  const { valueSpan, editor, value, td } = activeEditor;
  editor?.remove();
  td?.classList.remove('dcblock-cell-editing');
  if (valueSpan) {
    valueSpan.classList.remove('is-editing');
    valueSpan.textContent = value;
  }
  activeEditorRef.current = null;
}

function createEditableStringsCellContent(deviceId, channel, value) {
  const wrapper = document.createElement('span');
  wrapper.className = 'dcblock-editable-cell';

  const span = document.createElement('span');
  span.className = 'dcblock-editable-value';
  span.title = 'Click to edit value';
  span.dataset.deviceId = deviceId;
  span.dataset.channel = channel;
  span.textContent = value;

  wrapper.appendChild(span);
  return wrapper;
}

function startStringsCellEdit(td, deviceId, channel, value, activeEditorRef) {
  closeActiveStringsEditor(activeEditorRef);

  let wrapper = td.querySelector('.dcblock-editable-cell');
  let valueSpan = wrapper?.querySelector('.dcblock-editable-value');

  if (!wrapper || !valueSpan) {
    wrapper = createEditableStringsCellContent(deviceId, channel, value);
    td.replaceChildren(wrapper);
    valueSpan = wrapper.querySelector('.dcblock-editable-value');
  }

  valueSpan.classList.add('is-editing');
  td.classList.add('dcblock-cell-editing');

  const editor = document.createElement('span');
  editor.className = 'dcblock-editable-editor is-active';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'form-control form-control-sm dcblock-edit-input';
  input.value = value;
  input.setAttribute('inputmode', 'numeric');
  input.setAttribute('pattern', '[0-9]*');

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'btn btn-primary dcblock-confirm-edit';
  button.textContent = 'Modify';

  const errorEl = document.createElement('span');
  errorEl.className = 'dcblock-edit-error';
  errorEl.hidden = true;

  editor.append(input, button, errorEl);
  wrapper.appendChild(editor);
  input.addEventListener('input', () => {
    setStringsEditError(activeEditorRef.current, null);
  });
  input.focus();
  input.select();

  activeEditorRef.current = {
    td,
    wrapper,
    valueSpan,
    editor,
    deviceId,
    channel,
    value,
    input,
    button,
    errorEl,
  };
}

function toTableData(payload) {
  return {
    ...payload,
    headers: Array.isArray(payload?.headers) ? payload.headers : [],
    rows: Array.isArray(payload?.rows) ? payload.rows : [],
  };
}

function DCBlock() {
  const { plant: gemini } = usePlantData();
  const [blockSet, setBlockSet] = useState('a');
  const [selectedNormalizedDate, setSelectedNormalizedDate] = useState(() =>
    formatNormalizedInputDate(addDays(new Date(), -1))
  );
  const [tableView, setTableView] = useState('strings');
  const [tableData, setTableData] = useState({ headers: [], rows: [] });
  const latestNormalizedDate = addDays(new Date(), -1);
  const latestNormalizedLabel = formatNormalizedDisplayDate(latestNormalizedDate);
  const comparisonBaselineLabel = tableData.comparisonBaselineDate
    ? formatNormalizedDisplayDate(parseNormalizedInputDate(tableData.comparisonBaselineDate))
    : latestNormalizedLabel;
  const selectedNormalizedLabel = selectedNormalizedDate
    ? formatNormalizedDisplayDate(parseNormalizedInputDate(selectedNormalizedDate))
    : '';
  const showTableViewSelect = Boolean(selectedNormalizedDate);
  const [isLoading, setIsLoading] = useState(true);
  const tableHostRef = useRef(null);
  const dataTableRef = useRef(null);
  const activeEditorRef = useRef(null);
  const portfolioNameRef = useRef('');

  const handleBlockSetChange = (event) => {
    resetTableHost(tableHostRef, dataTableRef);
    setTableData({ headers: [], rows: [] });
    setIsLoading(true);
    setBlockSet(event.target.value);
  };

  const handleTableViewChange = (event) => {
    resetTableHost(tableHostRef, dataTableRef);
    setTableData({ headers: [], rows: [] });
    setIsLoading(true);
    setTableView(event.target.value);
  };

  const handleNormalizedDateChange = (event) => {
    const { value } = event.target;
    setSelectedNormalizedDate(value);

    if (!value && tableView !== 'strings') {
      resetTableHost(tableHostRef, dataTableRef);
      setTableData({ headers: [], rows: [] });
      setIsLoading(true);
      setTableView('strings');
    } else if (value && (tableView === 'normalized-previous' || tableView === 'normalized-comparison')) {
      resetTableHost(tableHostRef, dataTableRef);
      setTableData({ headers: [], rows: [] });
      setIsLoading(true);
    }
  };

  useEffect(() => {
    document.title = "Vivosol Plant Performance Insights Hub | Gemini / Diagnostics / DCBlock";
  }, []);

  useEffect(() => {
    let isMounted = true;
    const portfolioName = gemini.portfolio_name
      || gemini.plant_name?.toLowerCase().replace(/\s+/g, '-')
      || 'gemini';
    portfolioNameRef.current = portfolioName;

    const loadTableData = async () => {
      resetTableHost(tableHostRef, dataTableRef);
      setIsLoading(true);

      try {
        const params = getDCBlocksQueryParams(blockSet, tableView, selectedNormalizedDate);
        const data = await apiFetch(
          `/api/metrics/portfolio/${encodeURIComponent(portfolioName)}/dcblocks?${params.toString()}`
        );
        if (isMounted) {
          setTableData(toTableData(data));
        }
      } catch (error) {
        console.error('Failed to load block table data:', error);
        if (isMounted) {
          setTableData({ headers: [], rows: [] });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTableData();

    return () => {
      isMounted = false;
      resetTableHost(tableHostRef, dataTableRef);
    };
  }, [blockSet, tableView, selectedNormalizedDate, gemini.portfolio_name, gemini.plant_name]);

  useEffect(() => {
    const host = tableHostRef.current;
    if (isLoading || !host || tableData.rows.length === 0) {
      return undefined;
    }

    resetTableHost(tableHostRef, dataTableRef);
    activeEditorRef.current = null;

    const table = document.createElement('table');
    const isNormalizedTable = isNormalizedView(tableView);
    const isStringsTable = tableView === 'strings';
    table.className = isNormalizedTable
      ? 'table dcblock-benchmark-table'
      : 'table table-striped dcblock-strings-table';
    host.appendChild(table);

    const columnCount = tableData.headers.length;
    const isComparisonView = tableView === 'normalized-comparison';
    const { min, max, avg } = isNormalizedTable
      ? getNormalizedValueStats(tableData.rows)
      : { min: 0, max: 0, avg: 0 };
    const columns = tableData.headers.map((title, index) => {
      const column = { title: title || '' };

      if (isComparisonView && index > 0) {
        column.render = (cellData, type) => {
          if (type === 'sort' || type === 'type') {
            const num = Number(cellData);
            return Number.isNaN(num) ? 0 : num;
          }

          return formatComparisonPercent(cellData);
        };
      }

      if (isNormalizedTable && index > 0) {
        column.createdCell = (cell, cellData) => {
          if (cellData === '' || cellData == null) {
            return;
          }

          const num = Number(cellData);
          if (Number.isNaN(num)) {
            return;
          }

          cell.style.backgroundColor = getHeatmapColor(num, min, max, avg);
        };
      }

      if (isStringsTable && index > 0) {
        const channel = title || '';
        column.createdCell = (cell, cellData, rowData) => {
          if (cellData === '' || cellData == null) {
            return;
          }

          const deviceId = String(rowData?.[0] ?? '').trim();
          if (!deviceId || !channel) {
            return;
          }

          cell.replaceChildren(createEditableStringsCellContent(deviceId, channel, String(cellData)));
        };
      }

      return column;
    });
    const data = tableData.rows.map((row) => {
      const cells = [row.id, ...row.values];
      while (cells.length < columnCount) {
        cells.push('');
      }

      return cells.slice(0, columnCount);
    });

    dataTableRef.current = new DataTable(table, {
      data,
      columns,
      pageLength: 20,
      lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_LABELS],
      searching: false,
      paging: true,
      ordering: true,
      autoWidth: true,
      initComplete: () => {
        dataTableRef.current?.draw(false);
      },
      layout: {
        topStart: 'pageLength',
        topEnd: null,
        bottomStart: {
          info: {
            callback: formatTableInfo,
          },
        },
        bottomEnd: {
          paging: {
            numbers: 5,
          },
        },
      },
    });

    const handleTableClick = async (event) => {
      if (tableView !== 'strings') {
        return;
      }

      const confirmButton = event.target.closest('.dcblock-confirm-edit');
      if (confirmButton) {
        event.preventDefault();
        const activeEditor = activeEditorRef.current;
        if (!activeEditor || confirmButton !== activeEditor.button) {
          return;
        }

        const nextValue = activeEditor.input.value.trim();
        const validationError = validateNumStringsInput(nextValue);
        if (validationError) {
          setStringsEditError(activeEditor, validationError);
          activeEditor.input.focus();
          activeEditor.input.select();
          return;
        }

        setStringsEditError(activeEditor, null);

        if (nextValue === activeEditor.value) {
          closeActiveStringsEditor(activeEditorRef);
          return;
        }

        confirmButton.disabled = true;
        activeEditor.input.disabled = true;

        try {
          const response = await apiFetch(
            `/api/metrics/portfolio/${encodeURIComponent(portfolioNameRef.current)}/dcblocks/strings`,
            {
              method: 'PATCH',
              body: JSON.stringify({
                apiDeviceId: activeEditor.deviceId,
                channel: activeEditor.channel,
                numStrings: nextValue,
              }),
            }
          );

          const updatedValue = String(response.numStrings ?? nextValue);
          const channelIndex = parseChannelIndexFromHeader(activeEditor.channel);
          const tableApi = dataTableRef.current;
          const cellApi = tableApi?.cell(activeEditor.td);

          if (cellApi) {
            cellApi.data(updatedValue);
          }

          activeEditor.td.classList.remove('dcblock-cell-editing');
          activeEditor.td.replaceChildren(
            createEditableStringsCellContent(activeEditor.deviceId, activeEditor.channel, updatedValue)
          );
          activeEditorRef.current = null;

          if (channelIndex != null) {
            setTableData((previous) => ({
              ...previous,
              rows: updateRowStringsValue(
                previous.rows,
                activeEditor.deviceId,
                channelIndex,
                updatedValue
              ),
            }));
          }
        } catch (error) {
          console.error('Failed to update DC block strings value:', error);
          window.alert(error?.message || 'Failed to update value');
          confirmButton.disabled = false;
          activeEditor.input.disabled = false;
          activeEditor.input.focus();
        }

        return;
      }

      const editableValue = event.target.closest('.dcblock-editable-value');
      if (!editableValue) {
        return;
      }

      event.preventDefault();
      const td = editableValue.closest('td');
      if (!td) {
        return;
      }

      startStringsCellEdit(
        td,
        editableValue.dataset.deviceId,
        editableValue.dataset.channel,
        editableValue.textContent,
        activeEditorRef
      );
    };

    host.addEventListener('click', handleTableClick);

    return () => {
      host.removeEventListener('click', handleTableClick);
      activeEditorRef.current = null;
      resetTableHost(tableHostRef, dataTableRef);
    };
  }, [tableData, isLoading, tableView, blockSet]);

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <Marquee />
        <h1 className="mt-3 mb-0"><Link to={`${gemini.dashboard}`}><strong>{gemini.plant_name}</strong></Link> : {gemini.config} ({gemini.location})</h1>
        <Breadcrumbs labels={{ portfolioName: gemini.plant_name }} />
        <div id="dcblocks" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3 align-items-center">
              <span style={{ whiteSpace: 'nowrap' }}><FontAwesomeIcon icon={faMicrochip} /> DC BLOCK</span>
              <select
                className="form-select form-select-sm w-auto"
                value={blockSet}
                onChange={handleBlockSetChange}>
                <option value="a">A Blocks</option>
                <option value="b">B Blocks</option>
                <option value="d">D Blocks</option>
                <option value="e-sb1">E & SB1 Blocks</option>
              </select>
              <input
                type="date"
                id="normalized-previous"
                className="form-control form-control-sm w-auto"
                value={selectedNormalizedDate}
                onChange={handleNormalizedDateChange}
              />
              {showTableViewSelect && (
                <select
                  className="form-select form-select-sm w-auto"
                  value={tableView}
                  onChange={handleTableViewChange}>
                  <option value="strings">Strings</option>
                  <option value="normalized-current">Normalized - {latestNormalizedLabel}</option>
                  <option value="normalized-previous">Normalized - {selectedNormalizedLabel}</option>
                  <option value="normalized-comparison">Normalized - Comparison</option>
                </select>
              )}
              <strong>Solar PV DC Home Run Circuit Anomaly Detection</strong>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              &nbsp;
            </div>
          </div>
          <div className="body pt-3">
            {isLoading && <p className="mb-2">Loading...</p>}
            {!isLoading && tableData.rows.length === 0 && (
              <p className="mb-2">No data available</p>
            )}
            {selectedNormalizedDate && tableView === 'normalized-comparison' && (
              <p className="mb-2 text-end text-muted small">
                Comparing to Yesterday&apos;s data ({comparisonBaselineLabel})
              </p>
            )}
            <div
              ref={tableHostRef}
              className={`dcblock-table-host${isLoading ? ' is-loading' : ''}`}
            />
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default DCBlock;
